import { useState } from "react";
import { toast } from "sonner";
import { Send, MailOpen, Archive, ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const STATUS_LABELS = { nuovo: "Nuovo", letto: "Letto", risposto: "Risposto", archiviato: "Archiviato" };
const STATUS_COLORS = {
  nuovo: "text-gold border-gold/50", letto: "text-taupe border-hair",
  risposto: "text-emerald-700 border-emerald-700/40", archiviato: "text-taupe border-hair",
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function MessagesPanel({ contacts, reload }) {
  const [selected, setSelected] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [reply, setReply] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const openEdit = () => {
    setEditForm({ name: selected.name || "", email: selected.email || "", phone: selected.phone || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/contacts/${selected.id}`, editForm);
      toast.success("Contatto aggiornato");
      setSelected(data); setEditOpen(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const trash = async () => {
    if (!window.confirm(`Eliminare il messaggio di ${selected.name}?\n\nVerrà spostato nel Cestino (recuperabile per 30 giorni dalla sezione Richieste).`)) return;
    try {
      await api.post("/admin/requests/trash", { items: [{ type: "contact", id: selected.id }] });
      toast.success("Messaggio spostato nel Cestino");
      setSelected(null); reload();
    } catch { toast.error("Eliminazione non riuscita"); }
  };

  const setStatus = async (c, status) => {
    try {
      const { data } = await api.patch(`/admin/contacts/${c.id}`, { status });
      toast.success(`Stato: ${STATUS_LABELS[status]}`);
      if (selected?.id === c.id) setSelected(data);
      reload();
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  const open = async (c) => {
    setSelected(c);
    const st = c.status || (c.read ? "letto" : "nuovo");
    if (st === "nuovo") {
      try {
        const { data } = await api.patch(`/admin/contacts/${c.id}`, { status: "letto" });
        setSelected(data);
        reload();
      } catch {}
    }
  };

  const openReply = () => {
    setReply({
      subject: `Re: ${selected.subject || "Il tuo messaggio a Giovanna Matino Interior"}`,
      body: `Gentile ${selected.name},\n\n\n\nCordiali saluti\nGiovanna Matino\nInterior Design Studio`,
    });
    setReplyOpen(true);
  };

  const sendReply = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/contacts/${selected.id}/reply`, reply);
      toast.success("Risposta inviata");
      setReplyOpen(false);
      reload();
      const { data } = await api.get("/admin/contacts");
      const cur = data.find((x) => x.id === selected.id);
      if (cur) setSelected(cur);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito — la bozza resta qui, riprova");
    } finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  if (selected) {
    const st = selected.status || (selected.read ? "letto" : "nuovo");
    return (
      <div className="space-y-5" data-testid="message-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => setSelected(null)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="message-back-btn">
            <ArrowLeft size={14} /> Tutti i messaggi
          </button>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-3 py-1.5 ${STATUS_COLORS[st]}`} data-testid="message-status-badge">{STATUS_LABELS[st]}</span>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6">
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="font-serif text-2xl" data-testid="message-sender">{selected.name}</div>
              <div className="text-sm text-taupe mt-1">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</div>
            </div>
            <div className="text-xs text-taupe">{fmtDate(selected.created_at)}</div>
          </div>
          {selected.subject && <div className="mt-4 text-sm font-medium" data-testid="message-subject">{selected.subject}</div>}
          <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="message-body">{selected.message}</p>
        </div>

        {(selected.replies || []).length > 0 && (
          <div className="bg-white border border-hair p-5 md:p-6" data-testid="message-replies">
            <div className="eyebrow mb-3">Storico risposte</div>
            {selected.replies.slice().reverse().map((r, i) => (
              <div key={i} className="border-b border-hair/60 last:border-0 py-3">
                <p className="text-xs text-taupe">{fmtDate(r.at)} · a {r.to} · {r.result}</p>
                <p className="text-sm font-medium mt-1">{r.subject}</p>
                <p className="text-sm text-taupe whitespace-pre-wrap mt-1">{r.body}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button onClick={openReply} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="message-reply-btn"><Send size={14} /> Rispondi</button>
          <button onClick={openEdit} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="message-edit-btn"><Pencil size={14} /> Modifica</button>
          {st !== "letto" && st !== "risposto" && (
            <button onClick={() => setStatus(selected, "letto")} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="message-read-btn"><MailOpen size={14} /> Segna come letto</button>
          )}
          {st !== "archiviato" && (
            <button onClick={() => setStatus(selected, "archiviato")} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="message-archive-btn"><Archive size={14} /> Archivia</button>
          )}
          <button onClick={trash} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="message-delete-btn"><Trash2 size={14} /> Elimina</button>
        </div>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="rounded-none max-w-md" data-testid="message-edit-dialog">
            <DialogHeader><DialogTitle className="font-serif">Modifica contatto</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-none mt-1" data-testid="message-edit-name" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="rounded-none mt-1" data-testid="message-edit-email" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Telefono</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-none mt-1" data-testid="message-edit-phone" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveEdit} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="message-edit-save">{busy ? "Salvataggio…" : "Salva"}</button>
                <button onClick={() => setEditOpen(false)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`}>Annulla</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
          <DialogContent className="rounded-none max-w-lg" data-testid="message-reply-dialog">
            <DialogHeader><DialogTitle className="font-serif">Rispondi a {selected.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Destinatario</Label>
                <Input value={selected.email} readOnly className="rounded-none mt-1 bg-bone" data-testid="reply-to" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Oggetto</Label>
                <Input value={reply.subject} onChange={(e) => setReply({ ...reply, subject: e.target.value })} className="rounded-none mt-1" data-testid="reply-subject" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Risposta</Label>
                <Textarea rows={8} value={reply.body} onChange={(e) => setReply({ ...reply, body: e.target.value })} className="rounded-none mt-1" data-testid="reply-body" />
                <p className="text-[11px] text-taupe mt-1">La firma dello Studio è già inclusa nel testo; contatti e recapiti vengono aggiunti automaticamente in calce all'email.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={sendReply} disabled={busy || !reply.body.trim()} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="reply-send-btn">
                  <Send size={14} /> {busy ? "Invio…" : "Invia"}
                </button>
                <button onClick={() => setReplyOpen(false)} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="reply-cancel-btn">Annulla</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="messages-list">
      {contacts.length === 0 && <div className="col-span-2 p-12 text-center text-taupe bg-white border border-hair">Nessun messaggio</div>}
      {contacts.map((c) => {
        const st = c.status || (c.read ? "letto" : "nuovo");
        return (
          <button key={c.id} onClick={() => open(c)} className="text-left bg-white border border-hair p-6 hover:border-gold transition-colors" data-testid={`message-row-${c.id}`}>
            <div className="flex justify-between items-baseline gap-2">
              <div className="font-serif text-xl truncate">{c.name}</div>
              <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 shrink-0 ${STATUS_COLORS[st]}`}>{STATUS_LABELS[st]}</span>
            </div>
            <div className="text-xs text-taupe mt-1">{c.email}{c.phone ? ` · ${c.phone}` : ""} · {fmtDate(c.created_at)}</div>
            {c.subject && <div className="mt-3 text-sm font-medium">{c.subject}</div>}
            <p className="mt-2 text-sm text-taupe leading-relaxed line-clamp-2">{c.message}</p>
            {(c.replies || []).length > 0 && <p className="mt-2 text-[11px] text-emerald-700">{c.replies.length} rispost{c.replies.length === 1 ? "a inviata" : "e inviate"}</p>}
          </button>
        );
      })}
    </div>
  );
}
